import React from 'react';
import { useOrganization } from '../../contexts/OrganizationContext';
import { Card } from '../ui/Card/Card';
import { Button } from '../ui/Button/Button';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Guards the authenticated tree against the zero-organization state.
 *
 * A user can reach it legitimately: invited accounts own nothing, so being
 * removed from the inviting organization leaves them with none. Without this
 * guard the app loads and then fails on every data call with
 * "No organization found for user".
 */
// Routes that must stay reachable with zero organizations - the profile page is
// where the workspace actually gets created, so blocking it would be a trap.
const EXEMPT_PATHS = ['/profile'];

export const RequireOrganization: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { organizations, loading, error, refreshOrganizations } = useOrganization();
  const navigate = useNavigate();
  const location = useLocation();

  if (loading) return null;

  if (EXEMPT_PATHS.some((p) => location.pathname.startsWith(p))) {
    return <>{children}</>;
  }

  if (!organizations || organizations.length === 0) {
    // An empty list has two genuinely different causes.
    // OrganizationContext.loadOrganizations only calls setOrganizations() on
    // success - on failure it leaves the existing list untouched and just
    // sets `error`. So `error` is only ambiguous-with-zero-orgs *here*,
    // where the list is actually empty; a user with a non-empty list whose
    // *later* refresh fails (e.g. AdminDashboard.tsx's refreshOrganizations()
    // call right after a successful org rename) falls through to the bottom
    // of this function and keeps their page - nothing about their state is
    // ambiguous, so they never see either card below.
    if (error) {
      return (
        <Card>
          <h2>Couldn't load your workspaces</h2>
          <p>
            Something went wrong loading your organizations. This is often
            temporary.
          </p>
          <Button variant="primary" onClick={() => refreshOrganizations()}>
            Try again
          </Button>
        </Card>
      );
    }

    return (
      <Card>
        <h2>No workspace yet</h2>
        <p>
          You're not a member of any organization right now. Create a free
          personal workspace to get started.
        </p>
        <Button variant="primary" onClick={() => navigate('/profile')}>
          Create personal workspace
        </Button>
      </Card>
    );
  }

  return <>{children}</>;
};
